import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import request from "./API";

// Genera el PDF de un ticket autorizado (botón "Imprimir" del tablero de
// seguimiento). Arma el gafete del ticket fuera de pantalla -- incluyendo
// los campos extra configurados para la sala y el dato de quién/cuándo lo
// autorizó (sacado del historial) -- lo rasteriza con html2canvas y lo
// inserta como imagen en un PDF con jsPDF. Al quedar todo el contenido
// como imagen (no como texto/objetos) el PDF no se puede editar; además
// se bloquea explícitamente el permiso de modificación del archivo.
//
// Se reutiliza en cualquier pantalla que necesite imprimir un ticket --
// solo requiere el objeto tal cual lo regresa SeguimientoAct/Listado u
// Obtener (idSolicitud, idSala, folio, nombreActividad, nombreSala,
// comentarios, fechaInicio/createDate, usuarioNombre, fotoUsuario,
// datosExtra).
export const imprimirTicketPDF = async (actividad) => {
    const idSolicitud = actividad.idSolicitud;

    // Campos extra configurados para la sala del ticket (si tiene).
    let camposExtraDefinicion = [];
    try {
        const resCampos = await request.get(`Sala/CamposExtra/${actividad.idSala}`);
        const data = resCampos?.data ?? resCampos ?? {};
        const crudo = data.camposextra ?? data.camposExtra;
        const definicion = crudo ? JSON.parse(crudo) : [];
        camposExtraDefinicion = Array.isArray(definicion) ? definicion : [];
    } catch {
        camposExtraDefinicion = [];
    }

    const datosExtraValores = (() => {
        if (!actividad.datosExtra) return {};
        try {
            return JSON.parse(actividad.datosExtra);
        } catch {
            return {};
        }
    })();

    // Del historial se saca quién autorizó el ticket y a qué hora: el
    // último evento cuyo tipoEvento sea "A" (Aceptado).
    let autorizadoPor = "";
    let autorizadoFecha = "";
    try {
        const resHistorial = await request.get(`SeguimientoAct/Historial/${idSolicitud}`);
        const historial = resHistorial?.data || resHistorial || [];
        const eventoAutorizacion = [...historial].reverse().find((h) => h.tipoEvento === "A");
        if (eventoAutorizacion) {
            autorizadoPor = eventoAutorizacion.usuarioNombre || "";
            autorizadoFecha = eventoAutorizacion.createDate
                ? new Date(eventoAutorizacion.createDate).toLocaleString()
                : "";
        }
    } catch {
        // Si no se puede leer el historial, el PDF se genera sin ese dato
        // en vez de bloquear la impresión.
    }

    const formatearFecha = (fecha) => {
        if (!fecha) return "";
        const f = new Date(fecha);
        return f.toLocaleDateString() + " " + f.toLocaleTimeString();
    };

    const camposExtraHtml = camposExtraDefinicion.length > 0
        ? `
        <div style="margin-top:15px;">
            <b>Información adicional</b>
            <table style="width:100%; border-collapse:collapse; margin-top:8px; font-size:13px;">
                ${camposExtraDefinicion.map((c) => `
                    <tr>
                        <td style="padding:5px 8px; border:1px solid #ddd; background:#f5f5f5; width:40%;">${c.etiqueta || c.campo}</td>
                        <td style="padding:5px 8px; border:1px solid #ddd;">${datosExtraValores[c.campo] || "—"}</td>
                    </tr>
                `).join("")}
            </table>
        </div>` : "";

    const autorizacionHtml = (autorizadoPor || autorizadoFecha)
        ? `
        <div style="margin-top:15px; padding:12px; border:1px solid #2e7d32; border-radius:6px; background:#eef7ee;">
            <b>Autorización</b><br/>
            Autorizado por: ${autorizadoPor || "—"}<br/>
            Fecha de autorización: ${autorizadoFecha || "—"}
        </div>` : "";

    // ---- Arma el contenido fuera de pantalla para poder capturarlo ----
    const contenedor = document.createElement("div");
    contenedor.style.position = "fixed";
    contenedor.style.top = "0";
    contenedor.style.left = "-10000px";
    contenedor.style.width = "800px";
    contenedor.style.background = "#ffffff";
    contenedor.style.fontFamily = "Arial, sans-serif";
    contenedor.style.padding = "30px";
    contenedor.style.boxSizing = "border-box";

    contenedor.innerHTML = `
        <div style="border:1px solid #000; border-radius:10px; padding:20px; position:relative;">
            <div style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-30deg);
                        font-size:60px; color:rgba(255,0,0,0.15); font-weight:bold; pointer-events:none; white-space:nowrap;">
                AUTORIZADO
            </div>

            <div style="display:flex; align-items:center; gap:15px;">
                <img src="${actividad.fotoUsuario ? `data:image/jpeg;base64,${actividad.fotoUsuario}` : ""}"
                     style="width:60px; height:60px; border-radius:50%; object-fit:cover;" />
                <div>
                    <div style="font-weight:bold;">${actividad.usuarioNombre || "Usuario"}</div>
                    <div style="font-size:12px; color:gray;">Folio: ${actividad.folio || actividad.idSolicitud}</div>
                </div>
            </div>

            <div style="margin-top:15px;"><b>Actividad:</b> ${actividad.nombreActividad || ""}</div>
            <div style="margin-top:15px;"><b>Sala:</b> ${actividad.nombreSala || ""}</div>

            <div style="margin-top:15px;">
                <b>Comentarios:</b>
                <div style="background:#f5f5f5; padding:10px; border-radius:5px; margin-top:5px;">
                    ${actividad.comentarios || "Sin comentarios"}
                </div>
            </div>

            <div style="margin-top:15px;"><b>Fecha inicio:</b> ${formatearFecha(actividad.fechaInicio || actividad.createDate)}</div>

            ${camposExtraHtml}
            ${autorizacionHtml}
        </div>
    `;

    document.body.appendChild(contenedor);

    try {
        const canvas = await html2canvas(contenedor, { scale: 2, useCORS: true });
        const imagen = canvas.toDataURL("image/png");

        // Contraseña de propietario aleatoria: no se comparte con el
        // usuario, solo sirve para que jsPDF module bloquee la edición
        // del archivo (el usuario puede abrirlo e imprimirlo libremente,
        // pero no modificarlo/copiar su contenido desde un editor de PDF
        // que respete estos permisos).
        const ownerPassword = Math.random().toString(36).slice(2) + Date.now().toString(36);

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
            encryption: {
                ownerPassword,
                userPermissions: ["print"],
            },
        });

        const anchoPagina = pdf.internal.pageSize.getWidth();
        const altoPagina = pdf.internal.pageSize.getHeight();
        const margen = 10;

        const propsImagen = pdf.getImageProperties(imagen);
        const anchoImagen = anchoPagina - margen * 2;
        const altoImagen = (propsImagen.height * anchoImagen) / propsImagen.width;
        const altoUtilPorPagina = altoPagina - margen * 2;

        if (altoImagen <= altoUtilPorPagina) {
            pdf.addImage(imagen, "PNG", margen, margen, anchoImagen, altoImagen);
        } else {
            // El ticket no cabe en una sola hoja (muchos campos extra,
            // comentario largo, etc.) -- se reparte en varias páginas
            // desplazando la misma imagen hacia arriba en cada una.
            let alturaRestante = altoImagen;
            let desplazamiento = 0;

            while (alturaRestante > 0) {
                pdf.addImage(imagen, "PNG", margen, margen - desplazamiento, anchoImagen, altoImagen);
                alturaRestante -= altoUtilPorPagina;
                desplazamiento += altoUtilPorPagina;

                if (alturaRestante > 0) {
                    pdf.addPage();
                }
            }
        }

        pdf.setProperties({ title: `Ticket ${actividad.folio || actividad.idSolicitud}` });
        pdf.save(`Ticket_${actividad.folio || actividad.idSolicitud}.pdf`);
    } finally {
        document.body.removeChild(contenedor);
    }
};
